import React, { useState, useEffect } from 'react';
import { usePayerWizardContext } from '../PayerWizardContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, ArrowRight, Plus, Trash2, Star } from 'lucide-react';
import { RULESET_SOURCES, type PlanConfig, type RulesetSource } from '@/lib/payer-wizard/types';
import type { RuleSet } from '@/lib/rules/rulesetTypes';

export function PayerStep2Plans() {
  const { 
    progress, 
    updatePlans, 
    addPlan, 
    removePlan, 
    validateStep2, 
    completeStep, 
    goToStep,
    fetchExistingRulesets,
  } = usePayerWizardContext();
  
  const [existingRulesets, setExistingRulesets] = useState<RuleSet[]>([]);
  const [newPlanName, setNewPlanName] = useState('');
  // Pre-select template based on payer type
  const defaultRulesetSource = progress?.payer_type === 'BWC' ? 'BWC_OH_BASE_2026' : 'COMMERCIAL_BASE_TEMPLATE_V1';
  const [newRulesetSource, setNewRulesetSource] = useState<RulesetSource>(defaultRulesetSource);
  const [newSourceId, setNewSourceId] = useState<string | null>(null);
  
  useEffect(() => {
    fetchExistingRulesets().then(setExistingRulesets);
  }, [fetchExistingRulesets]);
  
  // Update default when payer type changes
  useEffect(() => {
    if (progress?.payer_type === 'BWC') {
      setNewRulesetSource('BWC_OH_BASE_2026');
    } else {
      setNewRulesetSource('COMMERCIAL_BASE_TEMPLATE_V1');
    }
  }, [progress?.payer_type]);
  
  if (!progress) return null;
  
  const validation = validateStep2();
  
  const handleAddPlan = () => {
    if (!newPlanName.trim()) return;
    
    const plan: PlanConfig = {
      plan_name: newPlanName,
      is_default: progress.plans.length === 0,
      ruleset_source: newRulesetSource,
      source_id: newRulesetSource === 'EXISTING_PLAN' ? newSourceId : null,
    };
    
    addPlan(plan);
    setNewPlanName('');
    setNewRulesetSource('COMMERCIAL_BASE_TEMPLATE_V1');
    setNewSourceId(null);
  };
  
  const handleSetDefault = (index: number) => {
    const updated = progress.plans.map((p, i) => ({
      ...p,
      is_default: i === index,
    }));
    updatePlans(updated);
  };
  
  const handleContinue = () => {
    if (validation.isValid) {
      completeStep(2);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 2: Plan Setup</CardTitle>
        <CardDescription>
          Add plans under this payer and select ruleset templates to clone
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Existing Plans */}
        {progress.plans.length > 0 && (
          <div className="space-y-3">
            <Label>Configured Plans</Label>
            {progress.plans.map((plan, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
              >
                <div className="flex items-center gap-3">
                  {plan.is_default && (
                    <Badge variant="default" className="bg-primary">
                      <Star className="h-3 w-3 mr-1" />
                      Default
                    </Badge>
                  )}
                  <span className="font-medium">{plan.plan_name}</span>
                  <Badge variant="outline">
                    {RULESET_SOURCES.find(s => s.value === plan.ruleset_source)?.label || plan.ruleset_source}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {!plan.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(index)}
                    >
                      Set as Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => removePlan(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add New Plan */}
        <div className="border rounded-lg p-4 space-y-4">
          <Label className="text-base font-semibold">Add New Plan</Label>
          
          <div className="space-y-2">
            <Label htmlFor="plan_name">Plan Name</Label>
            <Input
              id="plan_name"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
              placeholder="e.g., PPO, HMO, POS"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Ruleset Source</Label>
            <Select
              value={newRulesetSource}
              onValueChange={(value: RulesetSource) => {
                setNewRulesetSource(value);
                if (value !== 'EXISTING_PLAN') {
                  setNewSourceId(null);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select ruleset source" />
              </SelectTrigger>
              <SelectContent>
                {RULESET_SOURCES.map((source) => (
                  <SelectItem key={source.value} value={source.value}>
                    <div>
                      <span className="font-medium">{source.label}</span>
                      <p className="text-xs text-muted-foreground">{source.description}</p>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {newRulesetSource === 'EXISTING_PLAN' && (
            <div className="space-y-2">
              <Label>Clone From Existing Ruleset</Label>
              <Select
                value={newSourceId || ''}
                onValueChange={setNewSourceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select existing ruleset" />
                </SelectTrigger>
                <SelectContent>
                  {existingRulesets.map((rs) => (
                    <SelectItem key={rs.id} value={rs.id}>
                      {rs.name} ({rs.payer?.name || 'Unknown Payer'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <Button 
            onClick={handleAddPlan} 
            disabled={!newPlanName.trim() || (newRulesetSource === 'EXISTING_PLAN' && !newSourceId)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Plan
          </Button>
        </div>
        
        {/* Validation Errors */}
        {!validation.isValid && validation.errors.length > 0 && (
          <div className="bg-destructive/10 text-destructive border border-destructive/30 rounded-lg p-3">
            <ul className="list-disc list-inside text-sm">
              {validation.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => goToStep(1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button onClick={handleContinue} disabled={!validation.isValid}>
            Continue to Rule Review
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
